<script setup lang="ts">
import UiLoading from "~/components/ui/Loading.vue";

const props = defineProps({
  type: {
    type: String as () => "primary" | "secondary",
    default: "primary",
  },
  variant: {
    type: String as () => "solid" | "outlined" | "soft" | "subtle" | "ghost",
    default: "solid",
  },
  icon: {
    type: String,
    default: "",
  },
  trailingIcon: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  href: {
    type: String,
    default: "",
  },
  target: {
    type: String as () => "_blank" | "_self" | "_parent" | "_top",
    default: "_self",
  },
  loading: {
    type: Boolean,
    default: false,
  }
});
const slots = useSlots();

const onClick = (): void => {
  if (!props.href) return;

  window.open(props.href, props.target);
};
</script>

<template>
  <button
    class="ui-button"
    :name="props.name"
    :aria-label="props.name"
    :title="props.name"
    :class="[
      `type-${props.type}`,
      `variant-${props.variant}`,
      { 'has-icon': props.icon, 'has-trailing-icon': props.trailingIcon, 'has-content': slots.default }
    ]"
    @click="onClick"
  >
    <div class="content">
      <span v-if="props.icon" class="icon">
        <Icon :name="props.icon" />
      </span>

        <span class="content" v-if="slots.default">
        <slot />
      </span>

        <span v-if="props.trailingIcon" class="icon">
        <Icon :name="props.trailingIcon" />
      </span>
    </div>

    <UiLoading size="6px" v-show="props.loading" />
  </button>
</template>

<style scoped lang="scss">
.ui-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 30px;
  border-radius: var(--small-border-radius);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: 0.2s;

  .content {
    gap: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  &:not(.has-content) {
    padding: 10px;
  }

  .icon {
    font-size: 24px;
    line-height: 0;
  }

  &.type-primary {
    --btn-bg: var(--btn-primary-background);
    --btn-hover-bg: var(--btn-primary-hover-background);
    --btn-text: var(--btn-primary-text-color);
    --btn-outline: var(--btn-primary-outline-color);
  }

  &.type-secondary {
    --btn-bg: var(--btn-secondary-background);
    --btn-hover-bg: var(--btn-secondary-hover-background);
    --btn-text: var(--btn-secondary-text-color);
    --btn-outline: var(--btn-secondary-outline-color);
  }

  ::v-deep(.ui-loading span) {
    background: rgb(var(--btn-text)) !important;
  }

  &.variant-solid {
    background: rgb(var(--btn-bg));
    color: rgb(var(--btn-text));
    border-color: transparent;

    &:hover {
      background: rgb(var(--btn-hover-bg));
    }
  }

  &.variant-outlined {
    background: transparent;
    color: rgb(var(--btn-text));
    border: 1px solid rgba(var(--btn-outline), 0.3);

    &:hover {
      background: rgba(var(--btn-hover-bg), 0.5);
    }
  }

  &.variant-soft {
    background: rgb(var(--btn-bg));
    color: rgb(var(--btn-text));
    border-color: transparent;

    &:hover {
      background: rgb(var(--btn-hover-bg));
    }
  }

  &.variant-subtle {
    background: transparent;
    color: rgb(var(--btn-text));
    border-color: rgba(var(--btn-outline), 0.3);

    &:hover {
      background: rgb(var(--btn-hover-bg));
    }
  }

  &.variant-ghost {
    background: transparent;
    color: rgb(var(--btn-text));
    border-color: transparent;

    &:hover {
      background: rgba(var(--btn-hover-bg), 0.5);
    }
  }
}
</style>